import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../api/api';
import {
  Activity,
  BarChart3,
  Calendar,
  CheckCircle2,
  Clock3,
  Gift,
  RefreshCw,
  Store,
  Target,
  TicketPercent,
  Trophy,
  Users,
  Zap,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const EMPTY_SUMMARY = {
  totalStores: 0,
  totalSpinsUsed: 0,
  totalSpinsRemaining: 0,
  activeStores: 0,
  totalWinners: 0,
  avgSpinsPerStore: 0,
  totalPrizesGiven: 0,
};

const EMPTY_DASHBOARD_SUMMARY = {
  totalStores: 0,
  totalSpins: 0,
  todayWinners: 0,
  prizeGiven: 0,
  averageSpins: 0,
  totalValidWinners: 0,
  winningDistribution: [],
  genderConfig: [],
};

const COLORS = [
  '#4f46e5',
  '#7c3aed',
  '#0891b2',
  '#059669',
  '#d97706',
  '#dc2626',
  '#db2777',
  '#0f766e',
];

const formatNumber = (value) =>
  Number(value || 0).toLocaleString();

const normalizeSubcategories = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') {
          return { name: item, count: 1 };
        }

        const name =
          item.subcategory ||
          item.subCategory ||
          item.wonSubcategory ||
          item.won_subcategory ||
          item.category ||
          item.name ||
          item.label ||
          '';

        const count = Number(
          item.count ??
            item.wins ??
            item.totalWins ??
            item.total_wins ??
            item.winnerCount ??
            item.quantity ??
            0
        );

        return {
          name: String(name || '').trim(),
          count: Number.isFinite(count) ? count : 0,
        };
      })
      .filter((item) => item.name);
  }

  if (typeof value === 'object') {
    return Object.entries(value)
      .map(([name, count]) => ({
        name,
        count: Number(count) || 0,
      }))
      .filter((item) => item.name);
  }

  return [];
};

const getStoreSubcategories = (store) =>
  normalizeSubcategories(
    store?.wonSubcategories ??
      store?.won_subcategories ??
      store?.subcategoriesWon ??
      store?.winnerSubcategories ??
      store?.winner_subcategories ??
      store?.subcategories
  );

const getDiscountRows = (discountData) => {
  const source = discountData?.data || discountData || {};
  return Array.isArray(source.list) ? source.list : [];
};

const getDiscountSummary = (discountData) => {
  const source = discountData?.data || discountData || {};

  if (Array.isArray(source.summary)) {
    return source.summary;
  }

  return Array.isArray(source) ? source : [];
};

const StatCard = ({
  icon,
  label,
  value,
  helper,
  tone = 'indigo',
}) => {
  const tones = {
    indigo: { card: 'from-indigo-50 via-white to-white border-indigo-100', icon: 'bg-indigo-100 text-indigo-700 ring-indigo-200', accent: 'bg-indigo-500' },
    emerald: { card: 'from-emerald-50 via-white to-white border-emerald-100', icon: 'bg-emerald-100 text-emerald-700 ring-emerald-200', accent: 'bg-emerald-500' },
    amber: { card: 'from-amber-50 via-white to-white border-amber-100', icon: 'bg-amber-100 text-amber-700 ring-amber-200', accent: 'bg-amber-500' },
    violet: { card: 'from-violet-50 via-white to-white border-violet-100', icon: 'bg-violet-100 text-violet-700 ring-violet-200', accent: 'bg-violet-500' },
    cyan: { card: 'from-cyan-50 via-white to-white border-cyan-100', icon: 'bg-cyan-100 text-cyan-700 ring-cyan-200', accent: 'bg-cyan-500' },
    rose: { card: 'from-rose-50 via-white to-white border-rose-100', icon: 'bg-rose-100 text-rose-700 ring-rose-200', accent: 'bg-rose-500' },
  };
  const toneStyles = tones[tone] || tones.indigo;

  return (
    <div className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br ${toneStyles.card} shadow-sm p-4 sm:p-5 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200`}>
      <div className={`absolute inset-x-0 top-0 h-1 ${toneStyles.accent}`} />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs sm:text-sm font-medium text-slate-500">
            {label}
          </p>
          <p className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1 tracking-tight">
            {value}
          </p>
          {helper && (
            <p className="text-[11px] sm:text-xs text-slate-400 mt-1">
              {helper}
            </p>
          )}
        </div>

        <div
          className={`w-10 h-10 rounded-xl ring-1 flex items-center justify-center shrink-0 shadow-sm ${toneStyles.icon}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
};

const ChartCard = ({
  title,
  subtitle,
  icon,
  children,
  action,
}) => (
  <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
    <div className="px-4 sm:px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 className="font-bold text-slate-800 flex items-center gap-2">
          {icon}
          <span>{title}</span>
        </h3>

        {subtitle && (
          <p className="text-xs text-slate-400 mt-1">
            {subtitle}
          </p>
        )}
      </div>

      {action}
    </div>

    <div className="p-3 sm:p-5">
      {children}
    </div>
  </section>
);

const EmptyChart = ({
  text = 'No data available for this period.',
}) => (
  <div className="h-[280px] flex items-center justify-center text-sm text-slate-400">
    {text}
  </div>
);

const Dashboard = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [summary, setSummary] =
    useState(EMPTY_SUMMARY);

  const [stores, setStores] = useState([]);
  const [dailyTrend, setDailyTrend] = useState([]);
  const [discountSummary, setDiscountSummary] =
    useState([]);
  const [discountList, setDiscountList] =
    useState([]);

  const [dashboardSummary, setDashboardSummary] =
    useState(EMPTY_DASHBOARD_SUMMARY);

  const [dashboardWinningDistribution, setDashboardWinningDistribution] =
    useState([]);

  const [dashboardGenderConfig, setDashboardGenderConfig] =
    useState([]);

  const [lastUpdated, setLastUpdated] =
    useState(null);
  const requestInFlight = useRef(false);
  const lastUpdatedRef = useRef(null);


  const applyDashboardSummary = useCallback((response) => {
    const data = response?.data?.data || {};

    const nextSummary = {
      ...EMPTY_DASHBOARD_SUMMARY,
      totalStores: Number(data.totalStores || 0),
      totalSpins: Number(data.totalSpins || 0),
      todayWinners: Number(data.todayWinners || 0),
      prizeGiven: Number(data.prizeGiven || 0),
      averageSpins: Number(data.averageSpins || 0),
      totalValidWinners: Number(data.totalValidWinners || 0),
      winningDistribution: Array.isArray(data.winningDistribution)
        ? data.winningDistribution
        : [],
      genderConfig: Array.isArray(data.genderConfig)
        ? data.genderConfig
        : [],
    };

    setDashboardSummary(nextSummary);
    setDashboardWinningDistribution(nextSummary.winningDistribution);
    setDashboardGenderConfig(nextSummary.genderConfig);

    const updatedAt = new Date();
    lastUpdatedRef.current = updatedAt;
    setLastUpdated(updatedAt);
  }, []);

  const loadDashboardSummary = useCallback(async () => {
    try {
      const dashboardRes = await api.get('/api/dashboard/summary');
      applyDashboardSummary(dashboardRes);
    } catch (err) {
      console.error('Dashboard summary fetch error:', err);

      if (!lastUpdatedRef.current) {
        setError(
          'Dashboard summary could not be loaded. Check /api/dashboard/summary.'
        );
      }
    }
  }, [applyDashboardSummary]);

  const loadDashboard = useCallback(async () => {
    if (requestInFlight.current) return;

    requestInFlight.current = true;
    setLoading(true);
    setError('');

    try {
      const today = new Date();
      const start = new Date();
      start.setDate(today.getDate() - 30);

      const formatDate = (date) => {
        const y = date.getFullYear();
        const m = String(
          date.getMonth() + 1
        ).padStart(2, '0');
        const d = String(
          date.getDate()
        ).padStart(2, '0');

        return `${y}-${m}-${d}`;
      };

      const startDate = formatDate(start);
      const endDate = formatDate(today);

      const [
        storeRes,
        trendRes,
        discountRes,
      ] = await Promise.all([
        api.post(
          '/api/reports/store-data',
          {}
        ),

        api.get(
          '/api/reports/daily-trend',
          {
            params: {
              startDate,
              endDate,
            },
          }
        ),

        api
          .get(
            '/api/reports/discount-stats'
          )
          .catch(() => ({ data: {} })),
      ]);

      const storesData =
        Array.isArray(
          storeRes.data?.stores
        )
          ? storeRes.data.stores
          : [];

      setStores(storesData);

      setSummary({
        ...EMPTY_SUMMARY,
        ...(storeRes.data?.summary || {}),
      });

      setDailyTrend(
        Array.isArray(
          trendRes.data?.trend
        )
          ? trendRes.data.trend
          : []
      );

      setDiscountSummary(
        getDiscountSummary(
          discountRes.data
        )
      );

      setDiscountList(
        getDiscountRows(
          discountRes.data
        )
      );

      const updatedAt = new Date();
      lastUpdatedRef.current = updatedAt;
      setLastUpdated(updatedAt);
    } catch (err) {
      console.error(
        'Dashboard fetch error:',
        err
      );

      setError(
        lastUpdatedRef.current
          ? 'Live refresh failed. Showing the last successfully loaded data.'
          : 'Dashboard data could not be loaded. Check the backend connection.'
      );
    } finally {
      requestInFlight.current = false;
      setLoading(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([
      loadDashboard(),
      loadDashboardSummary(),
    ]);
  }, [loadDashboard, loadDashboardSummary]);

  useEffect(() => {
    refreshAll();

    const heavyRefreshInterval = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadDashboard();
      }
    }, 30_000);

    const summaryRefreshInterval = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadDashboardSummary();
      }
    }, 10_000);

    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshAll();
      }
    };

    document.addEventListener(
      'visibilitychange',
      refreshWhenVisible
    );

    return () => {
      window.clearInterval(
        heavyRefreshInterval
      );
      window.clearInterval(
        summaryRefreshInterval
      );
      document.removeEventListener(
        'visibilitychange',
        refreshWhenVisible
      );
    };
  }, [refreshAll, loadDashboard, loadDashboardSummary]);

  const normalizedStores = useMemo(
    () =>
      stores.map((store) => ({
        ...store,
        spins: Number(
          store.spins_used ||
            store.spins ||
            0
        ),
        winners: Number(
          store.winners_count ||
            store.winners ||
            0
        ),
        spinsRemaining: Number(
          store.spins_remaining || 0
        ),
        spinLimit: Number(
          store.spinLimit || 0
        ),
      })),
    [stores]
  );

  const topStores = useMemo(
    () =>
      [...normalizedStores]
        .sort(
          (a, b) => b.spins - a.spins
        )
        .slice(0, 20)
        .map((store) => ({
          name:
            store.storeName || 'N/A',
          spins: store.spins,
          winners: store.winners,
        })),
    [normalizedStores]
  );

  const winnerSubcategories =
    dashboardWinningDistribution;

  const discountUsage = useMemo(
    () =>
      discountSummary
        .map((item) => {
          const total = Number(
            item.total_codes || 0
          );

          const used = Number(
            item.used_codes || 0
          );

          return {
            name: `${item.percentage ?? 0} PKR`,
            total,
            used,
            available: Math.max(
              total - used,
              0
            ),
          };
        })
        .sort(
          (a, b) => b.total - a.total
        )
        .slice(0, 8),
    [discountSummary]
  );

  const trendData = useMemo(
    () =>
      dailyTrend.map((item) => ({
        date: item.date,
        spins: Number(item.spins || 0),
      })),
    [dailyTrend]
  );

  const categoryConfig =
    dashboardGenderConfig;

  const totalTrendSpins =
    trendData.reduce(
      (sum, item) =>
        sum + item.spins,
      0
    );

  const peakDay = trendData.reduce(
    (best, item) =>
      item.spins >
      (best?.spins || 0)
        ? item
        : best,
    null
  );

  const activeStorePct =
    Number(summary.totalStores) > 0
      ? Math.round(
          (Number(
            summary.activeStores || 0
          ) /
            Number(
              summary.totalStores
            )) *
            100
        )
      : 0;

  const totalDiscountCodes =
    discountList.length > 0
      ? discountList.length
      : discountSummary.reduce(
          (sum, item) =>
            sum +
            Number(
              item.total_codes || 0
            ),
          0
        );

  const usedDiscountCodes =
    discountSummary.reduce(
      (sum, item) =>
        sum +
        Number(
          item.used_codes || 0
        ),
      0
    );

  const discountUsagePct =
    totalDiscountCodes > 0
      ? Math.round(
          (usedDiscountCodes /
            totalDiscountCodes) *
            100
        )
      : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* PAGE HEADER */}
        <div className="bg-gradient-to-r from-slate-950 via-indigo-950 to-indigo-800 rounded-3xl p-5 sm:p-7 text-white shadow-xl overflow-hidden relative">
          <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-white/10" />
          <div className="absolute right-20 -bottom-24 w-56 h-56 rounded-full bg-indigo-400/10" />

          <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 rounded-full px-3 py-1 text-xs font-semibold mb-3">
                <Zap size={13} />
                Executive Campaign Overview
              </div>

              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
                Complete Analytics Dashboard
              </h1>

              <p className="text-indigo-100 text-sm mt-2 max-w-2xl">
              </p>
            </div>

            <div className="flex items-center gap-2">
              {lastUpdated && (
                <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-indigo-100 bg-white/10 px-3 py-2 rounded-xl">
                  <Clock3 size={13} />
                  Updated{' '}
                  {lastUpdated.toLocaleTimeString()}
                </span>
              )}

              <button
                type="button"
                onClick={refreshAll}
                disabled={loading}
                className="inline-flex items-center gap-2 bg-white text-indigo-800 hover:bg-indigo-50 disabled:opacity-60 px-4 py-2.5 rounded-xl text-sm font-bold transition"
              >
                <RefreshCw
                  size={16}
                  className={
                    loading
                      ? 'animate-spin'
                      : ''
                  }
                />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {loading && !lastUpdated ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400">
            Loading executive dashboard...
          </div>
        ) : (
          <>
            {/* KPI CARDS */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              <StatCard
                icon={<Store size={20} />}
                label="Total Stores"
                value={formatNumber(
                  dashboardSummary.totalStores
                )}
                helper={`${activeStorePct}% currently active`}
              />



              <StatCard
                icon={<Trophy size={20} />}
                label="Total Spins"
                value={formatNumber(
                  dashboardSummary.totalSpins
                )}
                helper="All spins recorded in SpinHistory"
                tone="violet"
              />

              <StatCard
                icon={<Calendar size={20} />}
                label="Today's Winners"
                value={formatNumber(
                  dashboardSummary.todayWinners
                )}
                helper="Valid winners recorded today"
                tone="indigo"
              />

              <StatCard
                icon={<Gift size={20} />}
                label="Prizes Given"
                value={formatNumber(
                  dashboardSummary.prizeGiven
                )}
                helper="Redeemed winning prizes"
                tone="rose"
              />

              <StatCard
                icon={<BarChart3 size={20} />}
                label="Avg Spins / Store"
                value={Number(
                  dashboardSummary.averageSpins ||
                    0
                ).toFixed(1)}
                helper="Total spins divided by total stores"
                tone="cyan"
              />
            </div>

            {/* TREND + HEALTH */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div className="xl:col-span-2">
                <ChartCard
                  title="Daily Spin Trend"
                  subtitle="Daily spin volume for the selected period / last 30 days"
                  icon={
                    <Activity
                      size={18}
                      className="text-indigo-600"
                    />
                  }
                  action={
                    <div className="hidden sm:flex gap-2 text-xs">
                      <span className="px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 font-semibold">
                        Total{' '}
                        {formatNumber(
                          totalTrendSpins
                        )}
                      </span>

                      <span className="px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 font-semibold">
                        Peak{' '}
                        {formatNumber(
                          peakDay?.spins || 0
                        )}
                      </span>
                    </div>
                  }
                >
                  {trendData.length > 0 ? (
                    <ResponsiveContainer
                      width="100%"
                      height={320}
                    >
                      <AreaChart
                        data={trendData}
                        margin={{
                          top: 10,
                          right: 10,
                          left: -15,
                          bottom: 5,
                        }}
                      >
                        <defs>
                          <linearGradient
                            id="spinTrendFill"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="0%"
                              stopColor="#6366f1"
                              stopOpacity={0.35}
                            />
                            <stop
                              offset="100%"
                              stopColor="#6366f1"
                              stopOpacity={0.03}
                            />
                          </linearGradient>
                        </defs>

                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke="#e2e8f0"
                        />

                        <XAxis
                          dataKey="date"
                          tick={{
                            fontSize: 10,
                          }}
                          tickLine={false}
                          axisLine={false}
                        />

                        <YAxis
                          allowDecimals={false}
                          tick={{
                            fontSize: 10,
                          }}
                          tickLine={false}
                          axisLine={false}
                        />

                        <Tooltip />

                        <Area
                          type="monotone"
                          dataKey="spins"
                          name="Spins"
                          stroke="#4f46e5"
                          strokeWidth={3}
                          fill="url(#spinTrendFill)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <EmptyChart />
                  )}
                </ChartCard>
              </div>

              <ChartCard
                title="Campaign Health"
                subtitle="Current store and discount-code utilization"
                icon={
                  <Target
                    size={18}
                    className="text-emerald-600"
                  />
                }
              >
                <div className="space-y-5 py-2">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="font-semibold text-slate-700">
                        Active Stores
                      </span>
                      <span className="font-bold text-emerald-600">
                        {activeStorePct}%
                      </span>
                    </div>

                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 rounded-full transition-all"
                        style={{
                          width: `${Math.min(
                            100,
                            activeStorePct
                          )}%`,
                        }}
                      />
                    </div>

                    <p className="text-xs text-slate-400 mt-1">
                      {formatNumber(
                        summary.activeStores
                      )}{' '}
                      of{' '}
                      {formatNumber(
                        summary.totalStores
                      )}{' '}
                      stores active
                    </p>
                  </div>

                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="font-semibold text-slate-700">
                        Discount Codes Used
                      </span>
                      <span className="font-bold text-indigo-600">
                        {discountUsagePct}%
                      </span>
                    </div>

                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-500 rounded-full transition-all"
                        style={{
                          width: `${Math.min(
                            100,
                            discountUsagePct
                          )}%`,
                        }}
                      />
                    </div>

                    <p className="text-xs text-slate-400 mt-1">
                      {formatNumber(
                        usedDiscountCodes
                      )}{' '}
                      used of{' '}
                      {formatNumber(
                        totalDiscountCodes
                      )}{' '}
                      generated
                    </p>
                  </div>
                </div>
              </ChartCard>
            </div>

            {/* TOP STORES + WINNING DISTRIBUTION */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ChartCard
                title="Top 20 Stores by Activity"
                subtitle="Ranked by spins used"
                icon={
                  <Store
                    size={18}
                    className="text-indigo-600"
                  />
                }
              >
                {topStores.length > 0 ? (
                  <ResponsiveContainer
                    width="100%"
                    height={330}
                  >
                    <BarChart
                      data={topStores}
                      layout="vertical"
                      margin={{
                        top: 5,
                        right: 10,
                        left: 5,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        horizontal={false}
                        stroke="#e2e8f0"
                      />

                      <XAxis
                        type="number"
                        allowDecimals={false}
                        tick={{
                          fontSize: 10,
                        }}
                      />

                      <YAxis
                        type="category"
                        dataKey="name"
                        width={115}
                        tick={{
                          fontSize: 10,
                        }}
                      />

                      <Tooltip />

                      <Bar
                        dataKey="spins"
                        name="Spins"
                        fill="#4f46e5"
                        radius={[0, 6, 6, 0]}
                        maxBarSize={22}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyChart text="No store activity available." />
                )}
              </ChartCard>

              <ChartCard
                title="Winning Distribution"
                subtitle="Valid winners grouped by won subcategory from SpinHistory"
                icon={
                  <Trophy
                    size={18}
                    className="text-violet-600"
                  />
                }
              >
                {winnerSubcategories.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 items-center gap-2">
                    <ResponsiveContainer
                      width="100%"
                      height={300}
                    >
                      <PieChart>
                        <Pie
                          data={
                            winnerSubcategories
                          }
                          dataKey="value"
                          nameKey="name"
                          innerRadius={65}
                          outerRadius={105}
                          paddingAngle={3}
                        >
                          {winnerSubcategories.map(
                            (
                              entry,
                              index
                            ) => (
                              <Cell
                                key={
                                  entry.name
                                }
                                fill={
                                  COLORS[
                                    index %
                                      COLORS.length
                                  ]
                                }
                              />
                            )
                          )}
                        </Pie>

                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>

                    <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
                      {winnerSubcategories.map(
                        (
                          item,
                          index
                        ) => (
                          <div
                            key={
                              item.name
                            }
                            className="flex items-center justify-between gap-3 text-xs"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <span
                                className="w-2.5 h-2.5 rounded-full shrink-0"
                                style={{
                                  background:
                                    COLORS[
                                      index %
                                        COLORS.length
                                    ],
                                }}
                              />

                              <span className="truncate text-slate-600">
                                {
                                  item.name
                                }
                              </span>
                            </div>

                            <span className="font-bold text-slate-800">
                              {formatNumber(
                                item.value
                              )}
                            </span>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                ) : (
                  <EmptyChart text="No won-subcategory data returned by store-data." />
                )}
              </ChartCard>
            </div>

            {/* DISCOUNT UTILIZATION + GENDER */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ChartCard
                title="Discount Code Utilization"
                subtitle="Generated vs used codes by discount value"
                icon={
                  <TicketPercent
                    size={18}
                    className="text-amber-600"
                  />
                }
              >
                {discountUsage.length > 0 ? (
                  <ResponsiveContainer
                    width="100%"
                    height={320}
                  >
                    <BarChart
                      data={
                        discountUsage
                      }
                      margin={{
                        top: 10,
                        right: 10,
                        left: -15,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#e2e8f0"
                      />

                      <XAxis
                        dataKey="name"
                        tick={{
                          fontSize: 10,
                        }}
                      />

                      <YAxis
                        allowDecimals={false}
                        tick={{
                          fontSize: 10,
                        }}
                      />

                      <Tooltip />

                      <Legend />

                      <Bar
                        dataKey="total"
                        name="Generated"
                        fill="#cbd5e1"
                        radius={[5, 5, 0, 0]}
                      />

                      <Bar
                        dataKey="used"
                        name="Used"
                        fill="#f59e0b"
                        radius={[5, 5, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyChart text="No discount-code statistics available." />
                )}
              </ChartCard>

              <ChartCard
                title="Spin Configuration by Gender"
                subtitle="Configured category records â€” not customer demographics"
                icon={
                  <Users
                    size={18}
                    className="text-cyan-600"
                  />
                }
              >
                {categoryConfig.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 items-center gap-2">
                    <ResponsiveContainer
                      width="100%"
                      height={280}
                    >
                      <PieChart>
                        <Pie
                          data={
                            categoryConfig
                          }
                          dataKey="value"
                          nameKey="name"
                          innerRadius={60}
                          outerRadius={95}
                          paddingAngle={4}
                        >
                          {categoryConfig.map(
                            (entry, index) => (
                              <Cell
                                key={
                                  entry.name
                                }
                                fill={
                                  index ===
                                  0
                                    ? '#3b82f6'
                                    : '#ec4899'
                                }
                              />
                            )
                          )}
                        </Pie>

                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>

                    <div className="space-y-3">
                      {categoryConfig.map(
                        (item) => (
                          <div
                            key={
                              item.name
                            }
                            className="rounded-xl bg-slate-50 p-4"
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-semibold text-slate-600">
                                {
                                  item.name
                                }
                              </span>

                              <span className="text-xl font-bold text-slate-900">
                                {
                                  item.value
                                }
                              </span>
                            </div>

                            <p className="text-xs text-slate-400 mt-1">
                              Configured category records
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                ) : (
                  <EmptyChart text="No category configuration data available." />
                )}
              </ChartCard>
            </div>

            {/* PEAK SPIN DAY */}
            <section className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-violet-700 via-indigo-600 to-cyan-500 p-6 sm:p-8 text-white shadow-xl">
              <div className="absolute -right-10 -top-16 h-56 w-56 rounded-full bg-white/10" />
              <div className="absolute right-36 -bottom-24 h-48 w-48 rounded-full bg-cyan-200/15" />
              <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/15 px-3 py-1 text-xs font-semibold text-white/90">
                    <Calendar size={14} />
                    Campaign highlight
                  </div>
                  <h2 className="mt-3 text-xl font-bold sm:text-2xl">Peak Spin Day</h2>
                </div>
                <div className="rounded-2xl border border-white/20 bg-white/15 px-5 py-4 shadow-lg backdrop-blur-sm sm:min-w-[16rem]">
                  <p className="text-sm font-medium text-white/75">{peakDay?.date || 'No activity'}</p>
                  <p className="mt-1 text-3xl font-extrabold tracking-tight sm:text-4xl">{formatNumber(peakDay?.spins || 0)}</p>
                  <p className="mt-1 text-sm font-semibold text-white/85">spins recorded</p>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
